import type { DiscoverGameV2Response } from "libs/apiClient";
import { useState } from "react";
import { _ } from "i18n";
import Modal from "ui-kit/src/Modal/Modal";
import { ContentCell } from "../../ContentCell";
import { DiscoverGameAnalysisDialog } from "features/discover/DiscoverGameAnalysisDialog";
import { DISCOVER_GAME_V2_DETAIL_QUERY_KEY } from "./constants";
import client from "libs/client";
import { useToast } from "ui-kit";
import { getClientErrorMessage } from "common/src/utils/errorUtils";
import { formatSwappedState } from "common/src/gamePage/utils/swappedState";
import { canSwapDiscoverGameHomeAwayMapping, getCompletedSportlogiqAnalysis } from "./swapHomeAwayMappingEligibility";

interface DiscoverGameAnalysisTabProps {
	gameData: DiscoverGameV2Response;
}

function getSwapHomeAwayMappingErrorMessage(error: unknown): string {
	const clientMessage = getClientErrorMessage(error);
	if (clientMessage !== null && clientMessage !== "") {
		return clientMessage;
	}

	if (typeof error === "object" && error !== null && "json" in error) {
		const json = (error as { json?: unknown }).json;
		if (typeof json === "string" && json !== "") {
			return json;
		}
	}

	return _("discover.gameDetail.swapHomeAwayMapping.toast.failed");
}

export function DiscoverGameAnalysisTab({ gameData }: DiscoverGameAnalysisTabProps) {
	const { addSuccessToast, addErrorToast } = useToast();
	const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
	const sportlogiqAnalysis = getCompletedSportlogiqAnalysis(gameData.gameAnalysis);
	const canSwapHomeAwayMapping = canSwapDiscoverGameHomeAwayMapping(gameData);
	const swapHomeAwayMappingMutation = client.swapGameHomeAwayMapping.useMutation({
		onSuccess: () => {
			addSuccessToast(_("discover.gameDetail.swapHomeAwayMapping.toast.requested"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes(DISCOVER_GAME_V2_DETAIL_QUERY_KEY),
			});
		},
		onError: (error) => {
			addErrorToast(getSwapHomeAwayMappingErrorMessage(error));
		},
	});

	const analysisProps = [
		{
			key: "providerTag",
			title: _("discover.gameDetail.field.providerTag"),
			type: "string" as const,
			value: gameData.providerTag,
		},
		{
			key: "isAnalysisFinished",
			title: _("discover.gameDetail.field.isAnalysisFinished"),
			type: "boolean" as const,
			value: gameData.isAnalysisFinished,
		},
	];

	const handleSwapHomeAwayMapping = () => {
		if (canSwapHomeAwayMapping === false || swapHomeAwayMappingMutation.isLoading) {
			return;
		}

		if (window.confirm(_("discover.gameDetail.swapHomeAwayMapping.confirm")) === false) {
			return;
		}

		swapHomeAwayMappingMutation.mutate({ id: gameData.id });
	};

	return (
		<div className="discover-card discoverGameDetailV2-tabCard">
			<div className="discoverGameDetailV2-stackedFieldGrid cell-grid">
				{analysisProps.map((props) => (
					<ContentCell
						key={props.key}
						invalidationKey={DISCOVER_GAME_V2_DETAIL_QUERY_KEY}
						title={props.title}
						type={props.type}
						value={props.value}
					/>
				))}

				<ContentCell
					invalidationKey={DISCOVER_GAME_V2_DETAIL_QUERY_KEY}
					title={_("discover.gameDetail.field.swapped")}
					type="string"
					value={sportlogiqAnalysis?.swapped ?? null}
					formatter={formatSwappedState}
				/>

				<div className="detailCell">
					<button type="button" className="discoverGamesTableV3-columnBtn" onClick={() => setShowAnalysisDialog(true)}>
						{_("discover.gameDetail.analysis.sendForAnalysis")}
					</button>
				</div>
				{canSwapHomeAwayMapping && (
					<div className="detailCell">
						<button
							type="button"
							className="discoverGamesTableV3-columnBtn"
							disabled={swapHomeAwayMappingMutation.isLoading}
							onClick={handleSwapHomeAwayMapping}
						>
							{_("discover.gameDetail.swapHomeAwayMapping.button")}
						</button>
					</div>
				)}
			</div>

			<Modal
				show={showAnalysisDialog}
				onClose={() => setShowAnalysisDialog(false)}
				title={_("discover.gameDetail.analysis.title")}
				size="lg"
			>
				<DiscoverGameAnalysisDialog gameData={gameData} invalidationQueryKey={DISCOVER_GAME_V2_DETAIL_QUERY_KEY} />
			</Modal>
		</div>
	);
}
